import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { CardType, PaymentMethodCard } from '@sg/graphql-schema';

export function useCreditCardOptions(params: UseCreditCardOptionsParams) {
  const { shouldUseApplePay, paymentMethods } = params;

  const { formatMessage } = useIntl();

  const creditCardOptions: readonly CreditCardOption[] = useMemo(() => {
    const applePayOptions = shouldUseApplePay
      ? [
          {
            label: formatMessage(messages.applePay),
            value: APPLE_PAY,
            cardType: APPLE_PAY,
            paymentMethodType: PAYMENT_METHOD_CARD,
            isDefault: true,
          },
        ]
      : [];

    const regularOptions =
      paymentMethods.map((paymentMethod) => ({
        value: paymentMethod.id,
        label: getPaymentMethodLabel(paymentMethod),
        cardType: paymentMethod.cardType,
        isDefault: paymentMethod.isDefault,
        paymentMethodType: PAYMENT_METHOD_CARD,
      })) ?? [];

    return [
      {
        label: formatMessage(messages.selectPaymentMethod),
        value: PAYMENT_ADD,
        cardType: PAYMENT_ADD,
        paymentMethodType: PAYMENT_METHOD_CARD,
        isDefault: false,
      },
      ...applePayOptions,
      ...regularOptions,
    ];
  }, [shouldUseApplePay, formatMessage, paymentMethods]);

  return { creditCardOptions };
}

function getPaymentMethodLabel(paymentMethod: PaymentMethodCard) {
  if (paymentMethod.nickname) {
    return `${paymentMethod.nickname} - ${paymentMethod.description}`;
  }

  return paymentMethod.description ?? '';
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  applePay: {
    defaultMessage: 'Apple Pay',
    description: 'Stripe | Credit Card Options | Apple Pay',
  },
  selectPaymentMethod: {
    defaultMessage: 'Select payment method',
    description: 'Stripe | Credit Card Options | Select payment method',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const APPLE_PAY = 'ApplePay' as const;
const PAYMENT_ADD = 'ADD' as const;
const PAYMENT_METHOD_CARD = 'PaymentMethodCard' as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type UseCreditCardOptionsParams = {
  paymentMethods: readonly PaymentMethodCard[];
  shouldUseApplePay: boolean;
};

type CreditCardOption = Readonly<{
  value: string;
  label: string;
  cardType: CardType | 'ADD' | 'ApplePay';
  paymentMethodType: 'PaymentMethodCard';
  isDefault: boolean;
}>;
